/**
 * The bell's Invitations tab — everything a company asked the citizen and is
 * waiting on: its invitations, its requests for more on a relationship that
 * exists (D2) and its sponsorship offers (the invitation to be sponsored, 3 Oct
 * 2026), each labelled by kind, the ones put off with "Not now" included.
 *
 * Home's card shows an ask until the citizen says "Not now"; this list shows
 * it until they DECIDE, so putting one off never takes the choice away. One
 * put off says when it comes back to Home. Review opens the same dialog Home's
 * card does; Decline asks first. Both dialogs are the bell's (it closes its
 * panel to show them), so this list only reports which was chosen.
 */
import { Clock } from '@tonnie/assets';
import { useCountryName, useLocale, useTranslation } from '@tonnie/locale';
import type { MyPendingInvitation, MySponsorshipOffer } from '@tonnie/sdk';
import { Button, cn } from '@tonnie/ui';

import { CompanyMark } from './CompanyMark';

export function BellInvitationList({
  invitations,
  offers,
  onReview,
  onDecline,
  onReviewOffer,
  onDeclineOffer,
}: {
  invitations: readonly MyPendingInvitation[];
  offers: readonly MySponsorshipOffer[];
  onReview: (invitation: MyPendingInvitation) => void;
  onDecline: (invitation: MyPendingInvitation) => void;
  onReviewOffer: (offer: MySponsorshipOffer) => void;
  onDeclineOffer: (offer: MySponsorshipOffer) => void;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const countryName = useCountryName();
  const putOffUntil = (deferredUntil: string | null) =>
    deferredUntil ? t('invitations.list.deferredUntil', { date: formatDate(deferredUntil) }) : null;

  return (
    <ul className="divide-y divide-border/50" data-testid="bell-invitations">
      {invitations.map((invitation) => {
        // A company's request for more on a relationship that exists (D2):
        // what it asks, no destination and no expiry.
        if (invitation.request) {
          return (
            <BellAskRow
              key={invitation.id}
              testId={`bell-request-${invitation.id}`}
              company={invitation.companyName}
              logo={invitation.companyLogo}
              title={t('invitations.request.listTitle', { company: invitation.companyName })}
              detail={
                invitation.request.asks === 'checkup'
                  ? t('invitations.request.kindCheckup')
                  : t('invitations.request.kindTests')
              }
              putOff={invitation.deferredUntil !== null}
              when={putOffUntil(invitation.deferredUntil)}
              reviewLabel={t('invitations.card.review')}
              declineLabel={t('invitations.card.decline')}
              onReview={() => onReview(invitation)}
              onDecline={() => onDecline(invitation)}
              reviewTestId={`bell-request-review-${invitation.id}`}
              declineTestId={`bell-request-decline-${invitation.id}`}
            />
          );
        }
        const what =
          invitation.invitationKind === 'testit'
            ? t('testit.invite.kindTestit')
            : t('testit.invite.kindFull');
        const where = invitation.destinationCountry
          ? countryName(invitation.destinationCountry)
          : null;
        return (
          <BellAskRow
            key={invitation.id}
            testId={`bell-invitation-${invitation.id}`}
            company={invitation.companyName}
            logo={invitation.companyLogo}
            title={t('invitations.list.title', { company: invitation.companyName })}
            detail={where ? `${what} · ${where}` : what}
            putOff={invitation.deferredUntil !== null}
            when={
              putOffUntil(invitation.deferredUntil) ??
              (invitation.expiresAt
                ? t('invitations.list.expires', { date: formatDate(invitation.expiresAt) })
                : null)
            }
            reviewLabel={t('invitations.card.review')}
            declineLabel={t('invitations.card.decline')}
            onReview={() => onReview(invitation)}
            onDecline={() => onDecline(invitation)}
            reviewTestId={`bell-invitation-review-${invitation.id}`}
            declineTestId={`bell-invitation-decline-${invitation.id}`}
          />
        );
      })}
      {offers.map((offer) => (
        <BellAskRow
          key={offer.id}
          testId={`bell-offer-${offer.id}`}
          company={offer.companyName}
          logo={offer.companyLogo}
          title={t('sponsorships.offer.list.title', { company: offer.companyName })}
          detail={`${t('sponsorships.offer.list.kind')} · ${countryName(offer.journey.destinationCountry)}`}
          putOff={offer.deferredUntil !== null}
          // Offers never expire: one not put off says when it was sent.
          when={
            putOffUntil(offer.deferredUntil) ??
            t('sponsorships.offer.list.sent', { date: formatDate(offer.offeredAt) })
          }
          reviewLabel={t('sponsorships.offer.card.review')}
          declineLabel={t('sponsorships.offer.declineCta')}
          onReview={() => onReviewOffer(offer)}
          onDecline={() => onDeclineOffer(offer)}
          reviewTestId={`bell-offer-review-${offer.id}`}
          declineTestId={`bell-offer-decline-${offer.id}`}
        />
      ))}
    </ul>
  );
}

/** One company ask in the list: who, what, when — Review, and Decline (quietest). */
function BellAskRow(props: {
  testId: string;
  company: string;
  logo: string | null;
  title: string;
  detail: string;
  putOff: boolean;
  when: string | null;
  reviewLabel: string;
  declineLabel: string;
  onReview: () => void;
  onDecline: () => void;
  reviewTestId: string;
  declineTestId: string;
}) {
  return (
    <li
      className="flex items-start gap-3 px-4 py-3"
      data-testid={props.testId}
      data-deferred={props.putOff}
    >
      <CompanyMark name={props.company} logo={props.logo} />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-semibold leading-snug text-foreground">{props.title}</p>
        <p className="truncate text-xs text-muted-foreground">{props.detail}</p>
        {props.when && (
          <p
            className={cn(
              'flex items-center gap-1 text-xs font-medium',
              props.putOff ? 'text-primary-accessible' : 'text-muted-foreground'
            )}
          >
            <Clock className="h-3 w-3 shrink-0" aria-hidden="true" />
            {props.when}
          </p>
        )}
        <div className="flex items-center gap-1 pt-1">
          <Button type="button" size="sm" onClick={props.onReview} data-testid={props.reviewTestId}>
            {props.reviewLabel}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="link"
            className="text-muted-foreground"
            onClick={props.onDecline}
            data-testid={props.declineTestId}
          >
            {props.declineLabel}
          </Button>
        </div>
      </div>
    </li>
  );
}
